import React from "react";
import styled from "@emotion/styled";

const AccountHistory = ({ status, date, changes }) => {
  function formatCustomDate(oldDate) {
    const date = new Date(oldDate);
    const options = { day: "numeric", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options);
  }

  const parseChanges = (changesStr) => {
    if (!changesStr) return [];

    const changes = [];
    const parts = changesStr
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);

    for (const part of parts) {
      if (part.includes("changed from")) {
        const match = part.match(/(.+?) changed from '(.+?)' to '(.+?)'/);
        if (match) {
          const field = match[1].replace(/_/g, " ");
          changes.push({
            field,
            from: match[2],
            to: match[3],
            type: "changed",
          });
        }
      } else if (part.includes("added")) {
        const match = part.match(/(.+?) added '(.+?)'/);
        if (match) {
          const field = match[1].replace(/_/g, " ");
          changes.push({
            field,
            value: match[2],
            type: "added",
          });
        }
      }
    }

    return changes;
  };

  const formatFieldName = (field) => {
    return field
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const shouldShowChange = (change) => {
    const excludeFields = [
      "created_at",
      "updated_at",
      "_id",
      "__v",
      "hide_vendor_banner",
      "account_id",
    ];
    const fieldKey = change.field.replace(/ /g, "_");

    if (excludeFields.includes(fieldKey)) return false;
    if (change.type === "changed" && change.from === change.to) return false;
    if (
      change.type === "changed" &&
      change.from === "null" &&
      change.to === "null"
    )
      return false;

    return true;
  };

  const parsedChanges = parseChanges(changes);
  const displayChanges = parsedChanges.filter(shouldShowChange);

  return (
    <HistoryContainer>
      <LogoContainer status={status}>
        {status.startsWith("Account") ? (
          <svg
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.1875 4.25C9.1875 3.48438 8.75 2.77344 8.09375 2.36328C7.41016 1.98047 6.5625 1.98047 5.90625 2.36328C5.22266 2.77344 4.8125 3.48438 4.8125 4.25C4.8125 5.04297 5.22266 5.75391 5.90625 6.16406C6.5625 6.54688 7.41016 6.54688 8.09375 6.16406C8.75 5.75391 9.1875 5.04297 9.1875 4.25ZM3.5 4.25C3.5 3.01953 4.15625 1.87109 5.25 1.24219C6.31641 0.613281 7.65625 0.613281 8.75 1.24219C9.81641 1.87109 10.5 3.01953 10.5 4.25C10.5 5.50781 9.81641 6.65625 8.75 7.28516C7.65625 7.91406 6.31641 7.91406 5.25 7.28516C4.15625 6.65625 3.5 5.50781 3.5 4.25ZM2.21484 13.4375H11.7578C11.5117 11.7148 10.0352 10.375 8.23047 10.375H5.74219C3.9375 10.375 2.46094 11.7148 2.21484 13.4375ZM0.875 13.957C0.875 11.25 3.03516 9.0625 5.74219 9.0625H8.23047C10.9375 9.0625 13.125 11.25 13.125 13.957C13.125 14.3945 12.7422 14.75 12.3047 14.75H1.66797C1.23047 14.75 0.875 14.3945 0.875 13.957Z"
              fill="#5954DE"
            />
          </svg>
        ) : status.startsWith("Member") ||
          status.startsWith("Vendor") ||
          status.startsWith("Referral") ||
          status.startsWith("Update") ? (
          <svg
            width="12"
            height="15"
            viewBox="0 0 12 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.5 13.4375C9.71875 13.4375 9.9375 13.2461 9.9375 13V5.125H7.75C7.25781 5.125 6.875 4.74219 6.875 4.25V2.0625H2.5C2.25391 2.0625 2.0625 2.28125 2.0625 2.5V13C2.0625 13.2461 2.25391 13.4375 2.5 13.4375H9.5ZM0.75 2.5C0.75 1.54297 1.51562 0.75 2.5 0.75H7.01172C7.47656 0.75 7.91406 0.941406 8.24219 1.26953L10.7305 3.75781C11.0586 4.08594 11.25 4.52344 11.25 4.98828V13C11.25 13.9844 10.457 14.75 9.5 14.75H2.5C1.51562 14.75 0.75 13.9844 0.75 13V2.5Z"
              fill="#5954DE"
            />
          </svg>
        ) : (
          <></>
        )}
      </LogoContainer>
      <ContentWrapper>
        <TextContainer>
          <DetailsContainer>
            <VendorText>
              <StatusText status={status} style={{ fontWeight: "600" }}>
                {status}
              </StatusText>
            </VendorText>
            {displayChanges.length > 0 && (
              <ChangesWrapper>
                {displayChanges.map((change, idx) => (
                  <ChangeItem key={idx}>
                    {change.type === "added" ? (
                      <>
                        <FieldName>{formatFieldName(change.field)}:</FieldName>{" "}
                        <NewValue>{change.value}</NewValue>
                      </>
                    ) : (
                      <>
                        <FieldName>{formatFieldName(change.field)}</FieldName>{" "}
                        changed from <OldValue>{change.from}</OldValue> to{" "}
                        <NewValue>{change.to}</NewValue>
                      </>
                    )}
                  </ChangeItem>
                ))}
              </ChangesWrapper>
            )}
          </DetailsContainer>
          <DateText>{formatCustomDate(date)}</DateText>
        </TextContainer>
      </ContentWrapper>
    </HistoryContainer>
  );
};

const HistoryContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 8px;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 6px;
    gap: 6px;
  }
`;

const LogoContainer = styled.div`
  width: 36px;
  height: 32px;
  min-width: 36px;
  border-radius: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
  background-color: rgba(89, 84, 222, 0.1);

  @media (max-width: 480px) {
    width: 32px;
    height: 28px;
    min-width: 32px;

    svg {
      width: 12px;
      height: auto;
    }
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const TextContainer = styled.div`
  gap: 8px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const DateText = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 14px;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 11px;
    align-self: flex-start;
  }
`;

const VendorText = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const StatusText = styled.span`
  color: rgba(33, 37, 67, 1);
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ChangesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ChangeItem = styled.div`
  margin-left: 8px;
  font-size: 13px;
  font-family: Montserrat, sans-serif;
  font-weight: 400;
  line-height: 160%;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    margin-left: 4px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 150%;
  }
`;

const FieldName = styled.span`
  font-weight: 500;
  color: #5954de;
`;

const OldValue = styled.span`
  text-decoration: line-through;
  color: #999;
  word-break: break-word;
`;

const NewValue = styled.span`
  color: #333;
  font-weight: 500;
  word-break: break-word;
`;

export default AccountHistory;